/**
 * First paint from the KV snapshot, then hand over to the live socket.
 *
 * Order matters:
 *   1. read snapshot (inlined in the HTML by the Worker, or fetched from /api/.../snapshot)
 *   2. hydrate stores synchronously
 *   3. render React (first frame already has real data: no spinners, no skeletons)
 *   4. requestIdleCallback → open the WebSocket
 */
import { meetingStore } from "../stores/meetingStore";
import { followingStore } from "../stores/followingStore";
import { connectionStore } from "../stores/connectionStore";
import { LiveChannel } from "./liveClient";
import type { MeetingDelta, MeetingSnapshot, OrgDelta, OrgSnapshot } from "./protocol";

/** The Worker can inline the KV value as <script type="application/json" id="snapshot:…">. */
export async function readSnapshot<T>(inlineId: string, fallbackUrl: string): Promise<T> {
  const el = document.getElementById(inlineId);
  if (el?.textContent) return JSON.parse(el.textContent) as T;
  const res = await fetch(fallbackUrl, { credentials: "include" });
  if (!res.ok) throw new Error(`snapshot ${res.status}`);
  return (await res.json()) as T;
}

const afterPaint = (fn: () => void) =>
  "requestIdleCallback" in window ? window.requestIdleCallback(fn, { timeout: 500 }) : setTimeout(fn, 0);

/** live: false on the public meeting page without dashboard access (the live socket is for members; the snapshot is public). */
export async function bootMeeting(meetingId: string, { live = true }: { live?: boolean } = {}): Promise<LiveChannel> {
  const snap = await readSnapshot<MeetingSnapshot>(`snapshot:meeting:${meetingId}`, `/api/meetings/${meetingId}/snapshot`);
  meetingStore.getState().hydrate(snap);
  connectionStore.setState({ status: "snapshot", snapshotAt: snap.at });

  const channel = new LiveChannel({
    url: `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/live/meeting/${meetingId}`,
    channel: `meeting:${meetingId}`,
    getLastSeq: () => meetingStore.getState().seq,
    onSnapshot: (data) => {
      const fresh = data as MeetingSnapshot;
      meetingStore.getState().hydrate(fresh);
      if (fresh.segmentPages) void loadSegmentPages(meetingId, fresh.segmentPages);
    },
    onDelta: (seq, d) => meetingStore.getState().applyDelta(seq, d as MeetingDelta),
  });
  if (live) afterPaint(() => channel.start());
  if (snap.segmentPages) afterPaint(() => void allSegments(meetingId));
  return channel;
}

const loading = new Map<string, Promise<void>>();
/**
 * Every segment of the meeting in the store: its later pages fetched once (after paint, or at once when a search needs the
 * whole transcript: components/meeting/transcriptSearch.ts). Resolves when they're in.
 */
export function allSegments(meetingId: string, get: typeof fetch = fetch): Promise<void> {
  const pages = meetingStore.getState().segmentPages;
  if (!pages) return Promise.resolve();
  let p = loading.get(meetingId);
  if (!p) { p = loadSegmentPages(meetingId, pages, get); loading.set(meetingId, p); }
  return p;
}

/**
 * Very long meetings: the snapshot inlines the first window of the transcript; the rest arrives in pages after
 * paint (GET /api/meetings/:id/segments/:n, meeting-producer.js). Pages are merged by id and kept sorted by start
 * (the playhead's binary search needs it), so a live caption appended meanwhile can't push a page out.
 */
export async function loadSegmentPages(meetingId: string, pages: number, get: typeof fetch = fetch) {
  for (let n = 1; n <= pages; n++) {
    const res = await get(`/api/meetings/${meetingId}/segments/${n}`, { credentials: "include" }).catch(() => null);
    if (!res?.ok) return;
    const page = (await res.json()) as MeetingSnapshot["segments"];
    if (meetingStore.getState().meeting?.id !== meetingId) return; // the viewer moved on
    meetingStore.setState((s) => ({ segments: mergeSegments(s.segments, page) }));
  }
}

export function mergeSegments(have: MeetingSnapshot["segments"], more: MeetingSnapshot["segments"]): MeetingSnapshot["segments"] {
  const ids = new Set(have.map((x) => x.id));
  const add = more.filter((x) => !ids.has(x.id));
  if (!add.length) return have;
  const last = have[have.length - 1];
  if (!last || add[0].start >= last.start) return [...have, ...add];
  return [...have, ...add].sort((x, y) => x.start - y.start);
}

export async function bootFollowing(orgId: string, meetingOrder: string[]): Promise<LiveChannel> {
  const snap = await readSnapshot<OrgSnapshot>(`snapshot:org:${orgId}`, `/api/orgs/${orgId}/following/snapshot`);
  followingStore.getState().hydrate(snap, meetingOrder);
  connectionStore.setState({ status: "snapshot", snapshotAt: snap.at });

  const channel = new LiveChannel({
    url: `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/live/org/${orgId}`,
    channel: `org:${orgId}`,
    getLastSeq: () => followingStore.getState().seq,
    onSnapshot: (data) => followingStore.getState().hydrate(data as OrgSnapshot, followingStore.getState().meetingOrder),
    onDelta: (seq, d) => followingStore.getState().applyDelta(seq, d as OrgDelta),
  });
  afterPaint(() => channel.start());
  return channel;
}
